import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth.service';

type NavItem = {
  labelKey: string;
  icon: string;
  route?: string;
  danger?: boolean;
  permission?: string;
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  private auth = inject(AuthService);
  private _hasAdminAccess: boolean | null = null;
  constructor(public router: Router) {}

  readonly publicItems: NavItem[] = [
    { labelKey: 'NAV.APP_LAUNCHER', icon: 'grid', route: '/' },
    { labelKey: 'NAV.PROFILE', icon: 'user-circle', route: '/profile' },
    { labelKey: 'NAV.MY_SESSIONS', icon: 'clock', route: '/sessions' },
    { labelKey: 'NAV.CHANGE_PASSWORD', icon: 'key', route: '/change-password' },
  ];

  readonly adminItems: NavItem[] = [
    { labelKey: 'System Owner', icon: 'crown', route: '/system-owner', permission: 'perm:system:*' },
    { labelKey: 'NAV.USERS', icon: 'user', route: '/users', permission: 'perm:auth:*' },
    { labelKey: 'NAV.INSURANCE_USERS', icon: 'briefcase', route: '/insurance-users', permission: 'perm:auth:*' },
    { labelKey: 'NAV.PATIENT_USERS', icon: 'patient', route: '/patient-users', permission: 'perm:auth:*' },
    { labelKey: 'NAV.DOCTOR_USERS', icon: 'doctor', route: '/doctor-users', permission: 'perm:auth:*' },
    { labelKey: 'NAV.REFERRAL_LAB_USERS', icon: 'lab', route: '/referral-lab-users', permission: 'perm:auth:*' },
    { labelKey: 'NAV.GROUPS', icon: 'users', route: '/groups', permission: 'perm:auth:*' },
    { labelKey: 'NAV.ROLES_PERMS', icon: 'key', route: '/roles', permission: 'perm:auth:*' },
    { labelKey: 'NAV.PERMISSIONS', icon: 'lock', route: '/permissions', permission: 'perm:auth:*' },
    { labelKey: 'NAV.SYSTEMS', icon: 'app', route: '/systems', permission: 'perm:system:*' },
    { labelKey: 'NAV.SECURITY', icon: 'shield', route: '/settings', permission: 'perm:system:*' },
    { labelKey: 'NAV.SESSIONS_ADMIN', icon: 'clock', route: '/admin-sessions', permission: 'perm:auth:sessions.manage' },
    { labelKey: 'NAV.AUDIT_LOGS', icon: 'history', route: '/login-attempts', permission: 'perm:auth:*' },
  ];

  private _bottomItems: NavItem[] | null = null;
  get bottomItems(): NavItem[] {
    if (this._bottomItems === null) {
      this._bottomItems = [];
      if (this.hasAdminAccess()) {
        this._bottomItems.push({ labelKey: 'NAV.SETTINGS', icon: 'settings', route: '/settings', permission: 'perm:system:*' });
      }
      this._bottomItems.push({ labelKey: 'NAV.SIGN_OUT', icon: 'logout', danger: true });
    }
    return this._bottomItems;
  }

  // التحقق من صلاحية الوصول للإدارة - محفوظة
  hasAdminAccess(): boolean {
    if (this._hasAdminAccess === null) {
      this._hasAdminAccess = this.auth.hasPermission('perm:auth:*');
    }
    return this._hasAdminAccess ?? false;
  }

  // التحقق من صلاحية لعرض رابط معين - بسيط وبدون cache (لأنه نادراً ما يُستدعى)
  canShowItem(item: NavItem): boolean {
    if (!item.permission) return true;
    return this.auth.hasPermission(item.permission);
  }

  async signOut() {
    await this.auth.logout();
  }

  private piMap: Record<string, string> = {
    grid: 'pi pi-th-large',
    user: 'pi pi-user',
    'user-circle': 'pi pi-user',
    users: 'pi pi-users',
    key: 'pi pi-key',
    lock: 'pi pi-lock',
    app: 'pi pi-window-maximize',
    shield: 'pi pi-shield',
    clock: 'pi pi-clock',
    history: 'pi pi-history',
    settings: 'pi pi-cog',
    logout: 'pi pi-sign-out',
    briefcase: 'pi pi-briefcase',
    patient: 'pi pi-heart',
    doctor: 'pi pi-user-plus',
    lab: 'pi pi-building',
    crown: 'pi pi-star',
  };
  
  iconCls = (name: string) => this.piMap[name] ?? 'pi pi-circle';
  
  // TrackBy functions لمنع re-rendering غير ضروري
  trackByRoute(index: number, item: NavItem): string {
    return item.route || item.labelKey || index.toString();
  }
}
